import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash-es';
import { InvoiceUpdatedType, PusherEventName } from 'anchor-scout-common/types';
import { FullPageModalHeaderStart, Typography, Badge, ConfirmationModal, Callout, Button, useOverlayContext, useDevice, Box } from 'seagull';
import { getStatusLabel } from '~/business-logic/invoiceLogic';
import { InvoiceAccountingIntegrationStatus } from '~/components/InvoiceAccountingIntegrationStatus';
import { routes } from '~/constants';
import { useEventReporter, useIoContext, useStore, useStoreActions, useStoreStatic } from '~/contexts';
import { useUnmarkPaidExternally } from '~/hooks/invoice/useUnmarkPaidExternally';
import { useUnvoidInvoice } from '~/hooks/invoice/useUnvoidInvoice';
import { useAttachFiles } from '~/hooks/useAttachFiles';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { Intent } from '~/infra/access-control';
import { handlePromise, handleRequest } from '~/infra/utils';
import { invoiceModel, relationshipModel, timeModel } from '~/models';
import { makeManualApprovalMethodNoticePeriod } from '~/models/approval-method-notice-period';
import { InvoiceOptimisticUpdateType } from '~/providers/InvoiceChangesObservableProvider';
import { BusinessChannelEventBinder } from '~/pusher';
import { selectors } from '~/store/state-selectors';
import { Accounting } from '~/types/api.model';
import { Attachments } from '../Attachments/Attachments';
import styles from './FullPageInvoice.module.scss';
import { MobileDivider, getBadgeSkin, getFooter, onRequestPartialRefundSuccess, onRequestPaymentConfirmed, showRequestPaymentMethodModal } from './FullPageInvoice.utils';
import FullPageInvoiceAccountingCallout from './FullPageInvoiceAccountingCallout';
import FullPageInvoiceAdditionalActions from './FullPageInvoiceAdditionalActions';
import { FullPageInvoiceCallout, getFullPageInvoiceCallouts, ResolvePaymentCallout } from './FullPageInvoiceCallout';
import InvoiceLineItemsTable from './InvoiceLineItemsTable/InvoiceLineItemsTable';
import RequestPartialRefund from './RequestPartialRefund';
import StopPaymentModal from './StopPaymentModal';
const approvePaymentLoader = 'approve-payment';
const retryChargeLoader = 'retry-charge';
const payNowLoader = 'pay-now';
const stopPaymentLoader = 'stopPayment';
const resumePaymentLoader = 'resumePayment';
export default function FullPageInvoiceWithData(props) {
    const { relationship, isVendor, showQboIntegration, showInvoiceQboSection, showInvoiceXeroSection, showXeroIntegration, contacts, serviceTemplates, refresh, onRefresh, onRedirectToPaymentMethods } = props;
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const { api, logBI } = useStoreActions();
    const history = useHistory();
    const { reportButtonClick } = useEventReporter();
    const { getNow } = useIoContext();
    const now = useMemo(()=>getNow(), []);
    const { getState } = useStoreStatic();
    const [invoice, setInvoice] = useState(props.invoice);
    const [disablingAutoApprove, setDisablingAutoApprove] = useState(false);
    const { startUnvoidInvoice, finishUnvoidInvoice } = useUnvoidInvoice();
    const { startUnmarkPaidExternally, finishUnmarkPaidExternally } = useUnmarkPaidExternally();
    const { attachments, openFilePicker, handleRemove: handleRemoveAttachment, isUploading: isUploadingAttachments } = useAttachFiles({
        initialAttachments: invoice.attachments,
        fetchAttachmentUrl: (attachmentId)=>api.invoice.getAttachmentDownloadUrl(invoice.id, attachmentId),
        onFilesUploaded: (uploadedData)=>api.invoice.attachFiles(invoice.id, uploadedData),
        onRemove: (attachment)=>api.invoice.removeAttachment(invoice.id, attachment.id)
    });
    const isPendingQBOIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.QBO));
    const isPendingXeroIntegrationSetup = useStore(selectors.integration.isPendingAccountingIntegrationSetup(Accounting.Xero));
    const { name, postalAddress } = relationship.vendor;
    const vendorLogoUrl = relationshipModel.vendorLogoUrl(relationship);
    const { addressOne, addressTwo, city, state, postalCode } = postalAddress;
    const { XERO_INTEGRATION = false, ATTACH_FILES } = useFeatureFlags();
    useEffect(()=>{
        setInvoice(props.invoice);
    }, [
        props.invoice
    ]);
    async function reloadInvoice() {
        refresh().then(()=>onRefresh({}));
    }
    const onDisableAutoApprove = async (invoice)=>{
        setDisablingAutoApprove(true);
        try {
            await api.invoice.disableAutoApprove(invoiceModel.id(invoice));
            overlayContext.showSuccessToast('Disabled auto-approval', 'disable-auto-approval-success');
            setInvoice(invoiceModel.setApprovalMethod(invoice, makeManualApprovalMethodNoticePeriod()));
        } catch (unused) {
            overlayContext.showErrorToast('Failed to disable auto-approval', 'disable-auto-approval-error');
        } finally{
            setDisablingAutoApprove(false);
        }
    };
    function onRetryCharge() {
        overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    skin: "danger",
                    title: "Retry charge of failed payment",
                    textAlignCenter: false,
                    content: /*#__PURE__*/ _jsx(_Fragment, {
                        children: /*#__PURE__*/ _jsxs(Typography, {
                            testId: "retry-charge-modal-text",
                            children: [
                                "You may retry the charge up to four times.",
                                ' ',
                                isVendor && 'It is recommended to inform your client before proceeding so they can verify funds are available.',
                                /*#__PURE__*/ _jsx("br", {}),
                                /*#__PURE__*/ _jsx("br", {}),
                                "Are you sure you want to charge invoice #",
                                invoice.formattedNumber,
                                " now?"
                            ]
                        })
                    }),
                    onPrimaryButtonClick: async ()=>{
                        overlayContext.startLoader(retryChargeLoader);
                        const { success } = await handleRequest(api.invoice.retryCharge(invoice.id));
                        if (!success) {
                            overlayContext.showErrorToast('Unable to retry, please try again', 'retry-charge-error');
                            overlayContext.stopLoader(retryChargeLoader);
                            return;
                        }
                        onRequestPaymentConfirmed({
                            invoice,
                            setInvoice,
                            onRefresh,
                            isVendor
                        });
                        overlayContext.stopLoader(retryChargeLoader);
                        dismissModal();
                    },
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onCloseButtonClick: ()=>dismissModal(),
                    testId: "retry-charge-confirmation-modal",
                    primaryButtonLabel: "Charge invoice #".concat(invoice.formattedNumber),
                    primaryButtonIntent: Intent.InvoiceRetryCharge,
                    secondaryButtonLabel: "Cancel"
                });
            }
        });
    }
    async function onApprovePayment() {
        logBI(10564, {
            invoiceId: invoice.id,
            relationshipId: relationship.id
        });
        reportButtonClick('billing-hub-client-approved-payment');
        overlayContext.startLoader(approvePaymentLoader);
        const { success } = await handleRequest(api.invoice.approve(invoice.id));
        if (success) {
            // Optimistic update.
            setInvoice(invoiceModel.setPending(invoice, now));
            onRefresh({
                optimisticUpdate: {
                    type: InvoiceOptimisticUpdateType.PaymentApproved,
                    invoiceId: invoice.id
                }
            });
        } else {
            overlayContext.showErrorToast("Failed to approve invoice ".concat(invoice.formattedNumber), 'approve-payment-error');
        }
        overlayContext.stopLoader(approvePaymentLoader);
    }
    async function onPayNow() {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    textAlignCenter: false,
                    onPrimaryButtonClick: async ()=>{
                        logBI(10561, {
                            invoiceId: invoice.id
                        });
                        overlayContext.startLoader(payNowLoader);
                        const { success } = await handlePromise(api.invoice.payNow(invoice.id));
                        if (!success) {
                            overlayContext.showErrorToast('Unable to pay now, please try again', 'pay-now-error');
                            overlayContext.stopLoader(payNowLoader);
                            return;
                        }
                        await handleRequest(reloadInvoice());
                        overlayContext.stopLoader(payNowLoader);
                        dismissModal();
                    },
                    onCloseButtonClick: ()=>dismissModal(),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    title: "Pay this invoice now?",
                    content: /*#__PURE__*/ _jsxs(Typography, {
                        children: [
                            "If you pay this invoice now, we will process the payment immediately. The due date will change to today's date, ",
                            timeModel.formatDate(now.getTime()),
                            ".",
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx("br", {}),
                            "If you prefer payment on the original due date ",
                            invoiceModel.dueDate(invoice),
                            ', click "Cancel".'
                        ]
                    }),
                    primaryButtonLabel: "I agree",
                    primaryButtonTestId: "invoice-pay-now-confirm",
                    secondaryButtonLabel: "Cancel"
                });
            }
        });
    }
    function onStopPayment() {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (props)=>/*#__PURE__*/ _jsx(StopPaymentModal, _object_spread_props(_object_spread({}, props), {
                    formattedNumber: invoice.formattedNumber,
                    onSubmit: async (reason)=>{
                        const logStopPaymentBI = (success)=>logBI(10562, {
                                invoiceId: invoice.id,
                                success
                            });
                        overlayContext.startLoader(stopPaymentLoader);
                        const { success } = await handleRequest(api.invoice.stopPayment(invoice.id, reason));
                        if (!success) {
                            logStopPaymentBI(false);
                            overlayContext.stopLoader(stopPaymentLoader);
                            overlayContext.showErrorToast("Failed to stop payment for invoice ".concat(invoice.formattedNumber), 'fpi-stop-payment-error');
                            return {
                                success: false
                            };
                        } else {
                            logStopPaymentBI(true);
                            await handleRequest(reloadInvoice());
                            overlayContext.stopLoader(stopPaymentLoader);
                            return {
                                success: true
                            };
                        }
                    }
                }))
        });
    }
    function onResumePayment() {
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: "Resume Payment",
                    textAlignCenter: false,
                    primaryButtonLabel: "Resume",
                    primaryButtonTestId: "submit-resume-payment",
                    secondaryButtonLabel: "Back",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    "You're about to resume a payment for invoice ",
                                    invoice.formattedNumber,
                                    "."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                children: "Any future invoices for this service provider will resume payment collection."
                            })
                        ]
                    }),
                    onSecondaryButtonClick: ()=>dismissModal(),
                    onPrimaryButtonClick: async ()=>{
                        const logResumePaymentBI = (success)=>logBI(10563, {
                                invoiceId: invoice.id,
                                success
                            });
                        overlayContext.startLoader(resumePaymentLoader);
                        const { success } = await handleRequest(api.invoice.resumePayment(invoice.id));
                        if (!success) {
                            logResumePaymentBI(false);
                            overlayContext.stopLoader(resumePaymentLoader);
                            overlayContext.showErrorToast("Failed to resume payment for invoice ".concat(invoice.formattedNumber), 'fpi-resume-payment-error');
                        } else {
                            logResumePaymentBI(true);
                            await handleRequest(reloadInvoice());
                            overlayContext.stopLoader(resumePaymentLoader);
                        }
                        dismissModal();
                    }
                });
            }
        });
    }
    async function onVoidInvoice() {
        var _getState_userInfo;
        const confirmed = await overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    title: "Void this invoice",
                    testId: "fpi-void-invoice-modal",
                    content: "Are you sure you want to void invoice ".concat(invoice.formattedNumber, "?"),
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false),
                    primaryButtonLabel: "I'm sure",
                    primaryButtonTestId: "fpi-void-invoice-confirm",
                    secondaryButtonLabel: "Cancel"
                });
            }
        });
        if (!confirmed) return;
        const loaderName = 'void';
        try {
            overlayContext.startLoader(loaderName);
            await api.invoice.void(invoice.id);
        } catch (err) {
            overlayContext.showErrorToast("Failed to void invoice ".concat(invoice.formattedNumber), 'fpi-void-invoice-error');
            return;
        } finally{
            overlayContext.stopLoader(loaderName);
        }
        // Optimistic update.
        const userId = ((_getState_userInfo = getState().userInfo) === null || _getState_userInfo === void 0 ? void 0 : _getState_userInfo.id()) || '';
        setInvoice(invoiceModel.setVoided(invoice, userId));
        onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.Voided,
                invoiceId: invoice.id,
                userId
            }
        });
    }
    function onRequestRefund() {
        const payment = invoiceModel.payment(invoice);
        if (!payment) return;
        overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(RequestPartialRefund, {
                    relationship: relationship,
                    id: invoice.id,
                    rawTotalAmount: invoiceModel.rawTotalAmount(invoice),
                    dismissModal: dismissModal,
                    payment: payment,
                    refreshInvoice: refresh,
                    onSuccess: (param)=>{
                        let { reason, refundAmount, refundFees } = param;
                        return onRequestPartialRefundSuccess({
                            invoice,
                            sellerId: relationshipModel.sellerID(relationship),
                            reason,
                            now: getNow(),
                            setInvoice,
                            onRefresh,
                            refundAmount,
                            refundFees
                        });
                    }
                });
            }
        });
    }
    function handleQboIntegrationChange(integration) {
        onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.QboIntegrationChanged,
                invoiceId: invoice.id,
                integration
            }
        });
    }
    function handleXeroIntegrationChange(integration) {
        onRefresh({
            optimisticUpdate: {
                type: InvoiceOptimisticUpdateType.XeroIntegrationChanged,
                invoiceId: invoice.id,
                xeroIntegration: integration
            }
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.root, {
            [styles.mobile]: isMobileDevice
        }),
        "data-testid": "fpi-content",
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "scout",
                eventName: PusherEventName.InvoiceUpdated,
                callback: (payload)=>{
                    if (invoice.id !== payload.invoice.id) {
                        return;
                    }
                    if (payload.type === InvoiceUpdatedType.Unvoided) {
                        setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
                        finishUnvoidInvoice(invoice.id);
                    } else if (payload.type === InvoiceUpdatedType.UnmarkedAsPaidExternally) {
                        setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
                        finishUnmarkPaidExternally(invoice.id);
                    } else if (payload.type === InvoiceUpdatedType.RefundFailed) {
                        setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
                    } else if (payload.type === InvoiceUpdatedType.XeroIntegrationStatusUpdated && XERO_INTEGRATION) {
                        setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
                    } else if (payload.type === InvoiceUpdatedType.QboIntegrationStatusUpdated) {
                        setInvoice(invoiceModel.fromDTO(isVendor, payload.invoice));
                    }
                }
            }),
            /*#__PURE__*/ _jsx(FullPageModalHeaderStart, {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: styles.invoiceHeader,
                    children: [
                        /*#__PURE__*/ _jsxs(Box, {
                            children: [
                                "Invoice #",
                                invoice.formattedNumber
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            outlined: true,
                            rounded: true,
                            skin: getBadgeSkin(invoice.displayStatus),
                            testId: "fpi-status",
                            "data-intent": invoice.displayStatus,
                            children: getStatusLabel(invoice.displayStatus)
                        }),
                        showInvoiceQboSection && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            className: styles.qboIcon,
                            testId: "fpi-qbo-icon",
                            invoiceIntegration: invoiceModel.qboIntegration(invoice),
                            placement: "right",
                            accountingType: Accounting.QBO
                        }),
                        showInvoiceXeroSection && /*#__PURE__*/ _jsx(InvoiceAccountingIntegrationStatus, {
                            className: styles.xeroIcon,
                            testId: "fpi-xero-icon",
                            invoiceIntegration: invoiceModel.xeroIntegration(invoice),
                            placement: "right",
                            accountingType: Accounting.Xero
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.calloutsSection,
                children: [
                    showRequestPaymentMethodModal(invoice, relationship) && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-missing-pm",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            skin: "primaryOutline",
                            testId: "fpi-add-pm",
                            onClick: onRedirectToPaymentMethods,
                            children: "Add payment method"
                        }),
                        children: "For this invoice to get paid through Anchor, a payment method must be connected"
                    }),
                    invoiceModel.isPaymentMethodUnverified(invoice) && !relationshipModel.isTerminated(relationship) && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-unverified-pm",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            skin: "primaryOutline",
                            testId: "fpi-verify-pm",
                            onClick: onRedirectToPaymentMethods,
                            children: "Verify payment method"
                        }),
                        children: "The payment method connected to this agreement is not yet verified"
                    }),
                    isPendingQBOIntegrationSetup && showQboIntegration && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-qbo-setup",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            testId: "fpi-go-to-qbo-setup",
                            skin: "primaryOutline",
                            onClick: ()=>history.push(routes.qboSetup),
                            children: "Go to setup"
                        }),
                        children: "Please finish setting up QBO by selecting the accounts in the integration setup page"
                    }),
                    isPendingXeroIntegrationSetup && showXeroIntegration && XERO_INTEGRATION && /*#__PURE__*/ _jsx(Callout, {
                        intent: "warning",
                        testId: "fpi-xero-setup",
                        withAction: /*#__PURE__*/ _jsx(Button, {
                            testId: "fpi-go-to-xero-setup",
                            skin: "primaryOutline",
                            onClick: ()=>history.push(routes.xeroSetup),
                            children: "Go to setup"
                        }),
                        children: "Please finish setting up Xero by selecting the accounts in the integration setup page"
                    }),
                    showInvoiceQboSection && /*#__PURE__*/ _jsx(FullPageInvoiceAccountingCallout, {
                        relationship: relationship,
                        invoice: invoice,
                        contacts: contacts,
                        serviceTemplates: serviceTemplates,
                        reloadInvoice: reloadInvoice,
                        onChange: handleQboIntegrationChange,
                        accounting: Accounting.QBO,
                        integration: invoiceModel.accounting({
                            accounting: Accounting.QBO,
                            invoice
                        }).integration
                    }),
                    showInvoiceXeroSection && /*#__PURE__*/ _jsx(FullPageInvoiceAccountingCallout, {
                        relationship: relationship,
                        invoice: invoice,
                        contacts: contacts,
                        serviceTemplates: serviceTemplates,
                        reloadInvoice: reloadInvoice,
                        onChange: handleXeroIntegrationChange,
                        accounting: Accounting.Xero,
                        integration: invoiceModel.accounting({
                            accounting: Accounting.Xero,
                            invoice
                        }).integration
                    }),
                    getFullPageInvoiceCallouts({
                        invoice,
                        relationship,
                        isVendor,
                        disabling: disablingAutoApprove,
                        now,
                        onRetryCharge,
                        onApprovePayment,
                        onPayNow,
                        onResumePayment,
                        onDisableAutoApprove
                    }).map((props)=>/*#__PURE__*/ _jsx(FullPageInvoiceCallout, _object_spread({}, props), props.id)),
                    invoiceModel.isPaymentActionRequired(invoice) && /*#__PURE__*/ _jsx(ResolvePaymentCallout, {
                        invoice: invoice,
                        isVendor: isVendor,
                        reloadInvoice: reloadInvoice
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.titleSection,
                children: [
                    !isMobileDevice && /*#__PURE__*/ _jsx(Typography, {
                        variant: "h2",
                        children: "Invoice"
                    }),
                    vendorLogoUrl && /*#__PURE__*/ _jsx("img", {
                        className: styles.logo,
                        src: vendorLogoUrl
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.titleSection,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.invoiceDetails,
                        children: [
                            [
                                'Client name:',
                                relationship.customer.name
                            ],
                            [
                                'Invoice number:',
                                invoiceModel.formattedNumber(invoice)
                            ],
                            [
                                'Issue date:',
                                invoiceModel.issuedOn(invoice)
                            ],
                            [
                                'Due date:',
                                invoiceModel.dueDate(invoice)
                            ]
                        ].map((param, idx)=>{
                            let [label, value] = param;
                            return /*#__PURE__*/ _jsxs(Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: label
                                    }, idx + 'l'),
                                    /*#__PURE__*/ _jsx(Typography, {
                                        children: value
                                    }, idx + 'v')
                                ]
                            }, idx);
                        })
                    }),
                    isMobileDevice && /*#__PURE__*/ _jsx(MobileDivider, {}),
                    /*#__PURE__*/ _jsx("div", {
                        className: styles.vendorDetails,
                        children: compact([
                            name,
                            addressOne,
                            addressTwo,
                            "".concat(city, ", ").concat(state, ", ").concat(postalCode)
                        ]).map((text, idx)=>/*#__PURE__*/ _jsx(Typography, {
                                children: text
                            }, idx))
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(InvoiceLineItemsTable, {
                invoice: invoice,
                isVendor: isVendor
            }),
            ATTACH_FILES && attachments.length > 0 && /*#__PURE__*/ _jsx(Attachments, {
                files: attachments,
                onRemove: handleRemoveAttachment
            }),
            getFooter(relationship, invoice).map((param)=>{
                let { id, text } = param;
                return /*#__PURE__*/ _jsx(Typography, {
                    variant: "paragraph2",
                    children: text
                }, id);
            }),
            /*#__PURE__*/ _jsx(FullPageInvoiceAdditionalActions, {
                invoice: invoice,
                relationship: relationship,
                isVendor: isVendor,
                reloadInvoice: reloadInvoice,
                onVoidInvoice: onVoidInvoice,
                onUnvoidInvoice: ()=>startUnvoidInvoice(invoice),
                onUnmarkPaidExternally: ()=>startUnmarkPaidExternally(invoice),
                onStopPayment: onStopPayment,
                onResumePayment: onResumePayment,
                onRequestRefund: onRequestRefund,
                onAttachFilesClick: openFilePicker,
                isUploadingAttachments: isUploadingAttachments
            })
        ]
    });
}
